import countBy from 'lodash/countBy';
import * as Yup from 'yup';

export const UPLOAD_MODE = {
  ADD: 'add',
  REPLACE: 'replace',
} as const;

export type UploadMode = (typeof UPLOAD_MODE)[keyof typeof UPLOAD_MODE];

type ContactRow = { email?: string | null };

type ImportedContact = { id?: string; email?: string; firstName?: string };

type ImportError = { email?: string; firstName?: string; reason?: string };

type ImportResult = {
  error?: string;
  data?: {
    created?: ImportedContact[];
    updated?: ImportedContact[];
    deleted?: ImportedContact[];
    errors?: ImportError[];
  };
};

export type FailedRow = { email: string; firstName: string; message?: string };

export type UploadResults = {
  new: ImportedContact[];
  updated: ImportedContact[];
  removed: ImportedContact[];
  failed: FailedRow[];
};

const normalizeEmail = (email?: string | null) => email?.trim().toLowerCase() ?? '';

/**
 * Finds the rows whose email (trimmed, case-insensitive) is shared with another row.
 *
 * @param rows - The contact rows to check; rows without an email are skipped.
 * @returns The indexes of every row in a duplicate group, in row order.
 */
export const findDuplicateEmailIndexes = (rows: ContactRow[]): number[] => {
  const emails = rows.map(row => normalizeEmail(row?.email));
  const counts = countBy(emails);
  return emails.flatMap((email, index) => (email && (counts[email] ?? 0) > 1 ? [index] : []));
};

/**
 * Builds a Yup array test that puts an error on the `email` of every row in a duplicate group,
 * so each row is flagged in the validation table rather than the whole array.
 *
 * @param message - The error shown on each duplicate row.
 * @returns A test for `Yup.array().test(...)`.
 */
export const uniqueEmailsTest = (message: string): Yup.TestConfig<ContactRow[] | undefined> => ({
  name: 'unique-emails',
  test(rows = []) {
    const errors = findDuplicateEmailIndexes(rows).map(index =>
      this.createError({ path: `${this.path}[${index}].email`, message }),
    );
    return errors.length === 0 || new Yup.ValidationError(errors);
  },
});

/**
 * Maps the `importContacts` thunk result to the rows of the results screen. A failed request
 * whose body lists no row errors becomes one failed row carrying the request's message.
 *
 * @param result - The thunk result: `{ data }` on success, `{ error, data }` on failure.
 * @returns Created, updated, removed and failed rows.
 */
export const toUploadResults = ({ error, data = {} }: ImportResult): UploadResults => {
  const { created = [], updated = [], deleted = [], errors = [] } = data;
  const failed = errors.map(({ email, firstName, reason }) => ({
    email: email || 'Unknown',
    firstName: firstName || '',
    message: reason,
  }));
  if (error && failed.length === 0) {
    failed.push({ email: 'N/A', firstName: '', message: error });
  }
  return { new: created, updated, removed: deleted, failed };
};
